import { Injectable } from '@angular/core';
import { SecureStorage } from '@aparajita/capacitor-secure-storage';
import { hasNativePlugin } from './platform';

const PLUGIN_NAME = 'SecureStorage';

/**
 * Platform-conditional secure storage.
 *
 * When the native Capacitor plugin is compiled into the app (Keychain on iOS,
 * Keystore on Android) all operations go through the plugin.  Otherwise
 * (web, or native build without the plugin) falls back to localStorage.
 *
 * The native-plugin check uses `hasNativePlugin()` instead of
 * `isNativePlatform()` to avoid an infinite proxy-delegation loop in the
 * `@aparajita` plugin when its Swift/Kotlin code is absent.
 */
@Injectable({ providedIn: 'root' })
export class SecureStorageService {
  readonly #useNative = hasNativePlugin(PLUGIN_NAME);

  async get(key: string): Promise<string | null> {
    if (this.#useNative) {
      try {
        const value = await SecureStorage.get(key);
        return typeof value === 'string'
          ? value
          : value != null
            ? String(value)
            : null;
      } catch {
        return null;
      }
    }
    return localStorage.getItem(key);
  }

  async set(key: string, value: string): Promise<void> {
    if (this.#useNative) {
      await SecureStorage.set(key, value);
      return;
    }
    localStorage.setItem(key, value);
  }

  async remove(key: string): Promise<void> {
    if (this.#useNative) {
      await SecureStorage.remove(key);
      return;
    }
    localStorage.removeItem(key);
  }

  async clear(): Promise<void> {
    if (this.#useNative) {
      await SecureStorage.clear();
      return;
    }
    // Web: no-op — localStorage clearing is handled by the Storage service
  }
}
